import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Modal, Tag, Table, Spin, Button, Avatar, message } from "antd";
import {
  ToolOutlined,
  BuildOutlined,
  UserOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/* ─── Styled ─────────────────────────────────────────────── */
const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;
const ProfileInfo = styled.div`
  flex: 1;
  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .sub {
    font-size: 13px;
    color: #666;
  }
`;
const EmpIdBadge = styled.span`
  background: #f0f0f0;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 12px;
  color: #444;
  font-weight: 400;
`;
const ExportBtn = styled(Button)`
  color: #2563eb;
  border: none;
  box-shadow: none;
  font-size: 13px;
  padding: 0;
  margin-left: auto;
  &:hover {
    color: #1d4ed8;
    background: transparent;
  }
`;

/* ── Stat Cards ── */
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const StatCard = styled.div`
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 12px 14px;
  .label {
    font-size: 12px;
    color: #888;
    margin-bottom: 4px;
  }
  .value {
    font-size: 16px;
    font-weight: 600;
    color: ${({ $color }) => $color || "#1a1a1a"};
  }
`;

/* ── Section ── */
const Section = styled.div`
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
`;
const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 14px;
`;
const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;
const AssetCard = styled.div`
  border: 1px solid #efefef;
  border-radius: 8px;
  padding: 14px;
  background: #fafafa;
`;
const AssetId = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
`;
const AssetMeta = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
`;
const AssetStats = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #555;
  flex-wrap: wrap;
  .stat {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;
const AssignedBadge = styled.div`
  font-size: 11px;
  color: #555;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 4px 8px;
  margin-top: 6px;
  line-height: 1.6;
  white-space: pre-line;
`;

const InServiceTag = styled(Tag)`
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
  border-radius: 12px;
  font-size: 11px;
  padding: 1px 8px;
`;

/* ─── Helpers ─────────────────────────────────────────────── */
const scoreColor = (s) => {
  if (s == null) return "#1a1a1a";
  if (s >= 8) return "#22c55e";
  if (s >= 5) return "#f59e0b";
  return "#ef4444";
};

const fmtDate = (d) => (d ? dayjs(d).format("Do MMM YYYY") : "—");

/* ─── Asset Card ──────────────────────────────────────────── */
const renderAssetCard = (asset, isPast = false) => {
  const numServices = Number(
    asset.curr_num_services ?? asset.num_services ?? 0
  );
  const replacements = Number(
    asset.curr_replacement_count ?? asset.replacement_count ?? 0
  );
  const serviceAmount = Number(
    asset.curr_total_service_amount ?? asset.total_service_amount ?? 0
  );
  const deductedAmount = Number(
    asset.curr_total_deducted_amount ?? asset.total_deducted_amount ?? 0
  );
  const assetName = asset.curr_assetname ?? asset.assetname ?? "—";
  const categoryName = asset.curr_categoryname ?? asset.categoryname ?? "";
  const categoryType = asset.curr_categorytype ?? asset.categorytype ?? "";
  const assignedOn = asset.curr_assigned_on ?? asset.assigned_on ?? null;
  const revokedOn = asset.curr_revoked_on ?? asset.revoked_on ?? null;
  const assignedBy = asset.curr_assigned_by ?? asset.assigned_by ?? "—";
  const revokedBy = asset.revoked_by ?? null;

  // assigned_on can be comma-separated dates — take the first
  const firstAssignedOn = assignedOn
    ? fmtDate(String(assignedOn).split(",")[0].trim())
    : "—";

  return (
    <AssetCard key={`${assetName}-${asset.assetid}`}>
      <AssetId>{assetName}</AssetId>
      <AssetMeta>
        {[categoryName, categoryType].filter(Boolean).join(" • ")}
      </AssetMeta>

      {(numServices > 0 || replacements > 0) && (
        <AssetStats>
          {numServices > 0 && (
            <span className="stat">
              <ToolOutlined style={{ color: "#f59e0b" }} />
              {numServices} Service Event{numServices !== 1 ? "s" : ""}
            </span>
          )}
          {replacements > 0 && (
            <span className="stat">
              <BuildOutlined style={{ color: "#ef4444" }} />
              {replacements} Part Replacement{replacements !== 1 ? "s" : ""}
            </span>
          )}
        </AssetStats>
      )}

      {serviceAmount > 0 && (
        <div style={{ fontSize: 12, color: "#555", marginBottom: 2 }}>
          Total Service Amount: <strong>${serviceAmount}</strong>
        </div>
      )}
      {deductedAmount > 0 && (
        <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
          Deducted from Employee: <strong>${deductedAmount}</strong>
        </div>
      )}

      <AssignedBadge>
        {`Assigned on ${firstAssignedOn}; By ${assignedBy}`}
        {isPast && revokedOn
          ? `\nRevoked on ${fmtDate(revokedOn)}; By ${revokedBy || "—"}`
          : ""}
      </AssignedBadge>
    </AssetCard>
  );
};

/* ═══════════════════════════════════════════════════════════
   EmployeeProfileModal
═══════════════════════════════════════════════════════════ */
const EmployeeProfileModal = ({ open, onClose, employee }) => {
  const userList = useSelector(getUserList);
  const contentRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    if (open && employee) fetchProfile();
    if (!open) setProfile(null);
  }, [open, employee]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const uid = employee?.userid || employee?.employeeid;
      const res = await CALL_API(`/assets/employee-history/${uid}`, "get");
      if (res.code === STATUS_CODE.SUCCESS) setProfile(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    try {
      setExportLoading(true);

      const element = contentRef.current;

      // Temporarily expand the container so nothing is clipped
      const prevMaxHeight = element.style.maxHeight;
      const prevOverflow = element.style.overflowY;
      element.style.maxHeight = "none";
      element.style.overflowY = "visible";

      const canvas = await html2canvas(element, {
        scale: 2, // 2× for crisp text
        useCORS: true, // allow cross-origin images (avatars)
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Restore scroll styles
      element.style.maxHeight = prevMaxHeight;
      element.style.overflowY = prevOverflow;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // mm

      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      // Scale image to fit page width
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      const ratio = usableWidth / (imgWidthPx * 0.264583); // px → mm at 96dpi
      const imgMmHeight = imgHeightPx * 0.264583 * ratio;

      // Paginate — slice the image into A4-height chunks
      let yOffset = 0;
      let page = 0;

      while (yOffset < imgMmHeight) {
        if (page > 0) pdf.addPage();

        // Source y in canvas pixels corresponding to yOffset mm
        const srcY = (yOffset / imgMmHeight) * imgHeightPx;
        const srcH = Math.min(
          (usableHeight / imgMmHeight) * imgHeightPx,
          imgHeightPx - srcY
        );

        // Crop this slice onto a temp canvas
        const slice = document.createElement("canvas");
        slice.width = imgWidthPx;
        slice.height = srcH;
        slice
          .getContext("2d")
          .drawImage(canvas, 0, srcY, imgWidthPx, srcH, 0, 0, imgWidthPx, srcH);

        const sliceH = (srcH / imgHeightPx) * imgMmHeight;
        pdf.addImage(
          slice.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableWidth,
          sliceH
        );

        yOffset += usableHeight;
        page++;
      }

      const fileName = `${name || "employee"}_asset_history.pdf`.replace(
        /\s+/g,
        "_"
      );
      pdf.save(fileName);
    } catch (err) {
      console.error("Export PDF error:", err);
      message.error("Failed to export PDF");
    } finally {
      setExportLoading(false);
    }
  };

  /* ── Derived from actual API response shape ── */
  const summary = profile?.historySummary ?? {};
  const currentAssets = profile?.current_assets ?? [];
  const pastAssets = profile?.past_assets ?? [];
  const ticketList = profile?.ticket_list ?? [];
  const linkedServices = profile?.linked_service ?? [];

  /* ── Employee info from Redux userList ── */
  const uid = employee?.userid || employee?.employeeid;
  const userObj = userList?.find((u) => u.userid === uid);
  const name = userObj?.fullname || employee?.fullname || employee?.name || "";
  const empId =
    userObj?.userid || employee?.userid || employee?.employeeid || "";
  const role = userObj?.jobtitle || employee?.rolename || "";
  const dept = employee?.departmentname || "";
  const avatar = userObj?.profileurl || null;

  /* ── Stat values from historySummary ── */
  const currentAssetCount = summary.current_asset ?? currentAssets.length;
  const pastAssetCount = summary.past_asset ?? pastAssets.length;
  const numServices = summary.num_services ?? linkedServices.length;
  const numTickets = summary.num_tickets ?? ticketList.length;
  const totalServiceCostUSD = Number(summary.total_service_amount_usd ?? 0);
  const totalDeductedUSD = Number(summary.total_deducted_amount_usd ?? 0);

  // Maintenance score: 10 - num_services (min 0)
  const mainScore = Math.max(0, 10 - (summary.num_services ?? 0));

  /* ── Tickets table columns ── */
  const ticketCols = [
    {
      title: "Ticket",
      dataIndex: "ticketid",
      key: "ticketid",
      width: 80,
      render: (v) => `AT${v}`,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (v) => (
        <span title={v}>{v?.length > 22 ? v.slice(0, 22) + "…" : v}</span>
      ),
    },
    {
      title: "Applied on",
      dataIndex: "applied_on",
      key: "applied_on",
      render: fmtDate,
    },
    { title: "Asset", dataIndex: "assetname", key: "assetname" },
    { title: "Priority", dataIndex: "priority", key: "priority" },
  ];

  /* ── Service events table columns ── */
  const serviceCols = [
    {
      title: "Asset",
      key: "asset",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.assetsname || "—"}</div>
        </div>
      ),
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      render: (v) => userList?.find((t) => t?.userid === v)?.fullname || "—",
    },
    {
      title: "Start Date",
      dataIndex: "service_start_date",
      key: "start",
      render: fmtDate,
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, r) => {
        if (!r.service_start_date) return "—";
        const end = r.service_end_date ? dayjs(r.service_end_date) : dayjs();
        const diff = end.diff(dayjs(r.service_start_date), "day");
        return <span>⏱ {diff === 0 ? "Less than a day" : `${diff}d`}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "service_status",
      key: "status",
      render: (v) =>
        v === "In Service" ? (
          <InServiceTag>In Service</InServiceTag>
        ) : (
          <span style={{ color: "#22c55e" }}>{v || "Completed"}</span>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={700}
      styles={{
        body: { maxHeight: "82vh", overflowY: "auto", padding: "20px 24px" },
      }}
    >
      <Spin spinning={loading}>
        <div ref={contentRef}>
          {/* ── Profile Header ── */}
          <ProfileHeader>
            <Avatar size={56} src={avatar} icon={<UserOutlined />} />
            <ProfileInfo>
              <h2>
                {name || "—"}
                {empId && <EmpIdBadge>Emp ID: #{empId}</EmpIdBadge>}
                <ExportBtn
                  icon={<FilePdfOutlined />}
                  loading={exportLoading}
                  onClick={handleExportPDF}
                >
                  Export PDF
                </ExportBtn>
              </h2>
              <div className="sub">
                {[role, dept].filter(Boolean).join(" • ")}
              </div>
            </ProfileInfo>
          </ProfileHeader>

          {/* ── Stat Cards ── */}
          <StatGrid>
            <StatCard>
              <div className="label">Current Asset</div>
              <div className="value">{currentAssetCount}</div>
            </StatCard>
            <StatCard>
              <div className="label">Past Assets</div>
              <div className="value">{pastAssetCount}</div>
            </StatCard>
            <StatCard>
              <div className="label">Maintenance Score</div>
              <div className="value" style={{ color: scoreColor(mainScore) }}>
                {mainScore} / 10
              </div>
            </StatCard>
            <StatCard>
              <div className="label">Total Tickets Raised</div>
              <div className="value" style={{ color: "#ef4444" }}>
                {numTickets}
              </div>
            </StatCard>
            <StatCard>
              <div className="label">Services Linked</div>
              <div className="value" style={{ color: "#ef4444" }}>
                {numServices}
              </div>
            </StatCard>
            <StatCard>
              <div className="label">Total Service Cost</div>
              <div className="value" style={{ color: "#ef4444" }}>
                ${totalServiceCostUSD.toLocaleString()}
              </div>
            </StatCard>
            <StatCard>
              <div className="label">Deducted So Far</div>
              <div className="value">${totalDeductedUSD.toLocaleString()}</div>
            </StatCard>
          </StatGrid>

          {/* ── Current Assets ── */}
          {currentAssets.length > 0 && (
            <Section>
              <SectionTitle>Current Asset</SectionTitle>
              <AssetGrid>
                {currentAssets.map((a) => renderAssetCard(a, false))}
              </AssetGrid>
            </Section>
          )}

          {/* ── Past Assets ── */}
          {pastAssets.length > 0 && (
            <Section>
              <SectionTitle>Past Assets</SectionTitle>
              <AssetGrid>
                {pastAssets.map((a) => renderAssetCard(a, true))}
              </AssetGrid>
            </Section>
          )}

          {/* ── Tickets ── */}
          {ticketList.length > 0 && (
            <Section>
              <SectionTitle>
                Tickets Raised &amp; Its Status ({ticketList.length})
              </SectionTitle>
              <div style={{ overflowX: "auto" }}>
                <Table
                  columns={ticketCols}
                  dataSource={ticketList}
                  pagination={false}
                  size="small"
                  rowKey={(r) => r.ticketid}
                  style={{ minWidth: 480 }}
                />
              </div>
            </Section>
          )}

          {/* ── Service Events ── */}
          {linkedServices.length > 0 && (
            <Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                }}
              >
                <SectionTitle style={{ marginBottom: 0 }}>
                  Service Events Linked To This Employee (
                  {linkedServices.length})
                </SectionTitle>
                <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>
                  Total Service Cost:{" "}
                  <span style={{ color: "#1a1a1a" }}>
                    ${totalServiceCostUSD.toLocaleString()}
                  </span>
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table
                  columns={serviceCols}
                  dataSource={linkedServices}
                  pagination={false}
                  size="small"
                  rowKey={(_, i) => i}
                  style={{ minWidth: 520 }}
                />
              </div>
            </Section>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default EmployeeProfileModal;
